import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// User Icon Component
const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// Lock Icon Component
const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <circle cx="12" cy="16" r="1" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Eye Icon Component
const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Eye Off Icon Component
const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "", show: false });
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [disableOnWrong, setDisableOnWrong] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Show welcome message on component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWelcome(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Hardcoded admin credentials
  const ADMIN_USERNAME = "admin";
  const ADMIN_PASSWORD = "password";

  // Reset wrong password disabling state when password input changes
  useEffect(() => {
    if (disableOnWrong) {
      setDisableOnWrong(false);
    }
  }, [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disableOnWrong) return;

    setIsLoading(true);
    setMessage({ text: "", type: "", show: false });

    // Validation
    if (!username.trim() || !password.trim()) {
      setMessage({
        text: "Please fill in all fields",
        type: "warning",
        show: true
      });
      setIsLoading(false);
      return;
    }

    // Add a small delay to show loading animation
    await new Promise(resolve => setTimeout(resolve, 800));

    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      setMessage({
        text: "Login successful! Redirecting to dashboard...",
        type: "success",
        show: true
      });

      // Wait a bit to show success message before redirecting
      setTimeout(() => {
        localStorage.setItem('admin_token', 'loggedin');
        navigate('/admin');
      }, 1000);
    } else {
      setMessage({
        text: "Invalid username or password. Please try again.",
        type: "error",
        show: true
      });
      setIsLoading(false);
      setDisableOnWrong(true);

      // Focus to password input for user convenience
      setTimeout(() => {
        if (passwordInputRef.current) {
          passwordInputRef.current.focus();
        }
      }, 200);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Auto-dismiss messages after 5 seconds (except success messages)
  useEffect(() => {
    if (message.show && message.type !== "success") {
      const timer = setTimeout(() => {
        setMessage({ ...message, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Make the button disabled on wrong password until user types something else
  let isButtonDisabled = isLoading || disableOnWrong;

  const getMessageStyles = (type: string) => {
    const baseStyles = {
      padding: "0.75rem 1rem",
      borderRadius: "0.5rem",
      fontSize: "0.875rem",
      border: "1px solid"
    };

    switch (type) {
      case 'success':
        return {
          ...baseStyles,
          backgroundColor: "rgba(34, 197, 94, 0.1)",
          borderColor: "#22c55e",
          color: "#15803d"
        };
      case 'error':
        return {
          ...baseStyles,
          backgroundColor: "rgba(239, 68, 68, 0.1)",
          borderColor: "#ef4444",
          color: "#dc2626"
        };
      case 'warning':
        return {
          ...baseStyles,
          backgroundColor: "rgba(245, 158, 11, 0.1)",
          borderColor: "#f59e0b",
          color: "#d97706"
        };
      default:
        return {
          ...baseStyles,
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          borderColor: "#3b82f6",
          color: "#1e40af"
        };
    }
  };

  return (
    <div style={styles.container}>
      {/* Animated background elements */}
      <div style={styles.backgroundElements}>
        <div style={styles.blob1}></div>
        <div style={styles.blob2}></div>
        <div style={styles.blob3}></div>
      </div>

      <div style={styles.cardContainer}>
        {/* Login Card */}
        <div style={styles.loginCard}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.iconContainer}>
              <div style={styles.lockIcon}>
                <LockIcon />
              </div>
            </div>
            <h2 style={styles.title}>Welcome Back</h2>
            <p style={styles.subtitle}>Sign in to your account</p>

            {/* Welcome Message */}
            {showWelcome && (
              <div style={{
                marginTop: "1rem",
                padding: "0.75rem",
                backgroundColor: "rgba(59, 130, 246, 0.1)",
                borderRadius: "0.5rem",
                fontSize: "0.85rem",
                textAlign: "center",
                color: "#1e40af"
              }}>
                Welcome to the Admin Dashboard! Please enter your credentials to continue.
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={styles.form} autoComplete="off">
            {/* Username Field */}
            <div style={styles.fieldContainer}>
              <label htmlFor="username" style={styles.label}>Username</label>
              <div style={styles.inputWrapper}>
                <div style={styles.inputIcon}>
                  <UserIcon />
                </div>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  style={styles.input}
                  placeholder="Enter your username"
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password Field */}
            <div style={styles.fieldContainer}>
              <label htmlFor="password" style={styles.label}>Password</label>
              <div style={styles.inputWrapper}>
                <div style={styles.inputIcon}>
                  <LockIcon />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={styles.passwordInput}
                  placeholder="Enter your password"
                  ref={passwordInputRef}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  style={styles.eyeButton}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            {/* Message Component */}
            {message.show && (
              <div style={{
                ...getMessageStyles(message.type),
                marginBottom: "0.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}>
                <span>{message.text}</span>
                <button
                  onClick={() => setMessage({ ...message, show: false })}
                  style={{
                    background: "none",
                    border: "none",
                    color: "inherit",
                    cursor: "pointer",
                    padding: "0.25rem",
                    borderRadius: "0.25rem",
                    opacity: 0.7
                  }}
                >
                  ×
                </button>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isButtonDisabled}
              style={{
                ...styles.submitButton,
                opacity: isButtonDisabled ? 0.5 : 1,
                cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
                transform: isButtonDisabled ? 'none' : 'scale(1)'
              }}
              tabIndex={isButtonDisabled ? -1 : 0}
              onClick={isButtonDisabled
                ? (e) => {
                  e.preventDefault();
                  // Optionally show brief shake/focus on the password field
                  if (passwordInputRef.current) {
                    passwordInputRef.current.focus();
                  }
                }
                : undefined}
              onMouseDown={isButtonDisabled
                ? (e) => {
                  e.preventDefault();
                  if (passwordInputRef.current) {
                    passwordInputRef.current.focus();
                  }
                }
                : undefined}
            >
              {isLoading ? (
                <div style={styles.loadingContent}>
                  <div style={styles.spinner}></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        input:focus {
          outline: none !important;
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2) !important;
        }

        button[type="submit"]:hover:not(:disabled) {
          transform: scale(1.05) !important;
          background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%) !important;
        }

        button[type="submit"]:disabled {
          pointer-events: none !important;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
        }

        button[type="button"]:hover {
          background-color: #f3f4f6 !important;
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 35%, #f3e8ff 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
    position: "relative" as const,
    overflow: "hidden",
  },
  backgroundElements: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "hidden",
  },
  blob1: {
    position: "absolute" as const,
    top: "-10rem",
    right: "-10rem",
    width: "20rem",
    height: "20rem",
    backgroundColor: "#a78bfa",
    borderRadius: "50%",
    mixBlendMode: "multiply" as const,
    filter: "blur(40px)",
    opacity: 0.7,
    animation: "blob 7s infinite",
  },
  blob2: {
    position: "absolute" as const,
    bottom: "-10rem",
    left: "-10rem",
    width: "20rem",
    height: "20rem",
    backgroundColor: "#fde047",
    borderRadius: "50%",
    mixBlendMode: "multiply" as const,
    filter: "blur(40px)",
    opacity: 0.7,
    animation: "blob 7s infinite",
    animationDelay: "2s",
  },
  blob3: {
    position: "absolute" as const,
    top: "10rem",
    left: "10rem",
    width: "20rem",
    height: "20rem",
    backgroundColor: "#fb7185",
    borderRadius: "50%",
    mixBlendMode: "multiply" as const,
    filter: "blur(40px)",
    opacity: 0.7,
    animation: "blob 7s infinite",
    animationDelay: "4s",
  },
  cardContainer: {
    position: "relative" as const,
    width: "100%",
    maxWidth: "28rem",
  },
  loginCard: {
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(16px)",
    borderRadius: "1rem",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    padding: "2rem",
    transform: "scale(1)",
    transition: "all 0.5s ease",
    animation: "pulse 2s ease-in-out infinite",
  },
  header: {
    textAlign: "center" as const,
    marginBottom: "2rem",
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "1rem",
  },
  lockIcon: {
    width: "4rem",
    height: "4rem",
    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    animation: "pulse 2s ease-in-out infinite",
  },
  title: {
    fontSize: "1.875rem",
    fontWeight: "bold",
    background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    marginBottom: "0.5rem",
  },
  subtitle: {
    color: "#6b7280",
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "1.5rem",
  },
  fieldContainer: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.5rem",
  },
  label: {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: "500",
    color: "#374151",
  },
  inputWrapper: {
    position: "relative" as const,
  },
  inputIcon: {
    position: "absolute" as const,
    top: "50%",
    left: "0.75rem",
    transform: "translateY(-50%)",
    color: "#9ca3af",
    pointerEvents: "none" as const,
  },
  input: {
    width: "100%",
    paddingLeft: "2.5rem",
    paddingRight: "1rem",
    paddingTop: "0.75rem",
    paddingBottom: "0.75rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.5rem",
    outline: "none",
    transition: "all 0.2s ease",
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    backdropFilter: "blur(4px)",
    boxSizing: "border-box" as const,
  },
  passwordInput: {
    width: "100%",
    paddingLeft: "2.5rem",
    paddingRight: "3rem",
    paddingTop: "0.75rem",
    paddingBottom: "0.75rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.5rem",
    outline: "none",
    transition: "all 0.2s ease",
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    backdropFilter: "blur(4px)",
    boxSizing: "border-box" as const,
  },
  eyeButton: {
    position: "absolute" as const,
    top: "50%",
    right: "0.75rem",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "#9ca3af",
    cursor: "pointer",
    padding: "0.25rem",
    borderRadius: "0.375rem",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
  },
  submitButton: {
    width: "100%",
    paddingTop: "0.75rem",
    paddingBottom: "0.75rem",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    color: "white",
    fontWeight: "600",
    borderRadius: "0.5rem",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    border: "none",
    cursor: "pointer",
    outline: "none",
    transition: "all 0.2s ease",
    transform: "scale(1)",
  },
  loadingContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  spinner: {
    width: "1.25rem",
    height: "1.25rem",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderTop: "2px solid white",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
};

export default Login;